import React, { useState, useEffect, createContext, useContext } from 'react';
import { AuthState, AuthResponse, AdminFormData, ClientLoginData, ClientAccountLoginData } from '../types/auth';
import { 
  authenticateAdmin, authenticateClient, authenticateClientAccount,
  saveAuthState, getAuthState, clearAuthState 
} from '../utils/auth';
import { supabase } from '../lib/supabase';

interface AuthContextType {
  authState: AuthState;
  loginAdmin: (credentials: AdminFormData) => Promise<void>;
  loginClient: (credentials: ClientLoginData) => Promise<void>;
  loginClientAccount: (credentials: ClientAccountLoginData) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    userType: null,
    isLoading: true,
    isAuthenticated: false
  });

  useEffect(() => {
    const { data: listener } = supabase.auth.onAuthStateChange(async (_event, session) => {
      try {
        if (session?.user) {
          // Keep previous user info if we have it in local storage
          const saved = getAuthState();
          setAuthState(prev => ({
            user: saved?.user || prev.user,
            userType: saved?.userType || prev.userType,
            isLoading: false,
            isAuthenticated: true
          }));
        } else {
          setAuthState({ user: null, userType: null, isLoading: false, isAuthenticated: false });
        }
      } catch (e) {
        setAuthState(prev => ({ ...prev, isLoading: false }));
      }
    });

    return () => {
      listener.subscription.unsubscribe();
    };
  }, []);

  const loginAdmin = async (credentials: AdminFormData) => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true }));
      const authResponse = await authenticateAdmin(credentials);
      setAuthState({
        user: authResponse.user,
        userType: authResponse.userType,
        isLoading: false,
        isAuthenticated: true
      });
      saveAuthState(authResponse);
    } catch (error) {
      setAuthState(prev => ({ ...prev, isLoading: false }));
      throw error;
    }
  };

  const loginClient = async (credentials: ClientLoginData) => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true }));
      const authResponse = await authenticateClient(credentials);
      setAuthState({
        user: authResponse.user,
        userType: authResponse.userType,
        isLoading: false,
        isAuthenticated: true
      });
      saveAuthState(authResponse);
    } catch (error) {
      setAuthState(prev => ({ ...prev, isLoading: false }));
      throw error;
    }
  };

  const loginClientAccount = async (credentials: ClientAccountLoginData) => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true }));
      const authResponse = await authenticateClientAccount(credentials);
      setAuthState({
        user: authResponse.user,
        userType: authResponse.userType,
        isLoading: false,
        isAuthenticated: true
      });
      saveAuthState(authResponse);
    } catch (error) {
      setAuthState(prev => ({ ...prev, isLoading: false }));
      throw error;
    }
  };

  const logout = () => {
    clearAuthState();
    supabase.auth.signOut();
    setAuthState({
      user: null,
      userType: null,
      isLoading: false,
      isAuthenticated: false
    });
  };

  const value = {
    authState,
    loginAdmin,
    loginClient,
    loginClientAccount,
    logout
  };

  return React.createElement(AuthContext.Provider, { value }, children);
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function useAdminAuth() {
  const { authState } = useAuth();
  if (!authState.isAuthenticated || authState.userType !== 'admin') {
    throw new Error('Admin authentication required');
  }
  return authState.user;
}

export function useClientAuth() {
  const { authState } = useAuth();
  if (!authState.isAuthenticated || authState.userType !== 'client') {
    throw new Error('Client authentication required');
  }
  return authState.user;
}

export function useClientAccountAuth() {
  const { authState } = useAuth();
  if (!authState.isAuthenticated || (authState.userType !== 'viewer' && authState.userType !== 'editor')) {
    throw new Error('Client account authentication required');
  }
  return authState.user;
}